import type { SyntaxMetricType } from '../contexts/SyntaxAnalysisContext';
import type { HierarchyProfile } from '../services/network-hierarchy-api.service';

export type NetworkAnalysisIndicatorId = SyntaxMetricType | 'structure' | 'population';

export const NETWORK_ANALYSIS_LABEL = 'Sintaxis Espacial';
export const DERIVED_ANALYSIS_LABEL = 'Prioridad Estructural';

export const SYNTAX_METRIC_LABELS: Record<SyntaxMetricType, string> = {
  integration_global: 'Integration global (N)',
  integration_global_raw: 'Integration global raw',
  integration_r1200: 'Integration 1200 m',
  integration_r800: 'Integration 800 m',
  choice_global: 'Choice global',
  choice_r1200: 'Choice 1200 m',
  choice_r800: 'Choice 800 m',
};

export const SYNTAX_METRIC_DESCRIPTIONS: Record<SyntaxMetricType, string> = {
  integration_global: 'Qué tan accesible queda un tramo desde el resto de la red completa.',
  integration_global_raw: 'Accesibilidad global antes del ajuste por alcance efectivo.',
  integration_r1200: 'Accesibilidad del tramo dentro de un entorno local de 1200 metros.',
  integration_r800: 'Accesibilidad del tramo dentro de un entorno local de 800 metros.',
  choice_global: 'Potencial de paso del tramo dentro de rutas eficientes a escala de toda la red.',
  choice_r1200: 'Potencial de paso del tramo en recorridos eficientes de hasta 1200 metros.',
  choice_r800: 'Potencial de paso del tramo en recorridos eficientes de hasta 800 metros.',
};

export const SYNTAX_METRIC_GROUPS: Array<{
  title: string;
  items: SyntaxMetricType[];
}> = [
  {
    title: 'Integration',
    items: ['integration_global', 'integration_r1200', 'integration_r800', 'integration_global_raw'],
  },
  {
    title: 'Choice',
    items: ['choice_global', 'choice_r1200', 'choice_r800'],
  },
];

export const NETWORK_ANALYSIS_INDICATOR_LABELS: Record<NetworkAnalysisIndicatorId, string> = {
  ...SYNTAX_METRIC_LABELS,
  structure: DERIVED_ANALYSIS_LABEL,
  population: 'Población por segmento',
};

export const NETWORK_ANALYSIS_INDICATOR_DESCRIPTIONS: Record<NetworkAnalysisIndicatorId, string> = {
  ...SYNTAX_METRIC_DESCRIPTIONS,
  structure: 'Síntesis derivada que resume el peso estratégico del tramo combinando lecturas globales y locales.',
  population: 'Población estimada por segmento usando radios censales e intersección con la red canónica.',
};

export const NETWORK_ANALYSIS_INDICATOR_GROUPS: Array<{
  title: string;
  items: NetworkAnalysisIndicatorId[];
}> = [
  ...SYNTAX_METRIC_GROUPS,
  {
    title: 'Síntesis estructural',
    items: ['structure'],
  },
  {
    title: 'Datos socioespaciales',
    items: ['population'],
  },
];

export const ANALYSIS_HELP_TEXT = {
  integration:
    'Integration mide qué tan fácil es alcanzar un tramo desde otros segmentos de la red.',
  choice:
    'Choice mide cuánto potencial de paso concentra un tramo dentro de rutas eficientes.',
  global:
    'Global usa la red completa y sirve para leer centralidad urbana a escala ciudad.',
  radius:
    'Los radios muestran comportamiento local. Sirven para comparar accesibilidad barrial y microcentralidades.',
  structure:
    'Prioridad Estructural resume qué tan estratégico es un tramo combinando accesibilidad global y local.',
};

export const HIERARCHY_PROFILE_LABELS: Record<HierarchyProfile, string> = {
  global: 'Global',
  local: 'Local',
  equilibrado: 'Equilibrado',
};

export const HIERARCHY_PROFILE_DESCRIPTIONS: Record<HierarchyProfile, string> = {
  global: 'Prioriza la accesibilidad a escala ciudad.',
  local: 'Prioriza la accesibilidad de proximidad en un radio de 1200 m.',
  equilibrado: 'Combina 50% lectura global y 50% lectura local.',
};

export const STRUCTURE_BAND_LABELS = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
} as const;
